import React, { useState } from 'react'
import { Card, CardContent, CardHeader } from '../ui/Card'
import {
  TRANSACTION_DIRECTION,
  TRANSACTION_TYPES,
} from '@/data/constants/constants'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import { Button, ButtonText } from '../ui/button'
import {
  ArrowPathRoundedSquareIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import useGetVault from '@/services/api/cache/useGetVault'
import { Description } from '../ui/Description'
import { formatDate } from 'date-fns'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu'
import useOpenNonModalWindow from '@/hooks/useOpenNonModalWndow'
import useGetTransactionRepeatOptions from '@/services/api/cash-flow/transaction/useGetTransactionRepeatOptions'
import useGetTransactionByVaultAndOffset from '@/services/api/cache/useGetTransactionByFilter'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import useGetModel from '@/services/api/model/useGetModel'
import useGetTransactionCategory from '@/services/api/transaction-category/useGetTransactionCategory'
import useDeleteTransaction from '@/services/api/cash-flow/transaction/useDeleteTransaction'

interface TransactionItemProps {
  transaction: any
}

export default function TransactionCard({ transaction }: TransactionItemProps) {
  const format = useNumberFormatter()

  const navigate = useNavigate()
  const location = useLocation()

  const { modelId } = useParams()

  const { action: deleteT, status: deleteStatus } = useDeleteTransaction()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showDeleteSeriesConfirmation, setShowDeleteSeriesConfirmation] =
    useState(false)

  const { data: vault, status: vaultStatus } = useGetVault({
    vaultId: transaction?.vault_id,
  })

  const { data: recurringSettings } = useGetTransactionRepeatOptions({
    recurring_setting_id: transaction?.recurring_setting_id,
    date: transaction?.transaction_date,
  })

  const { data: model, modelStatus } = useGetModel({
    id: transaction?.what_if_id,
  })

  const { data: category, isLoading: isLoadingCategory } =
    useGetTransactionCategory({ id: transaction?.transaction_category_id })
  const selectedRecurringSettings = recurringSettings?.find((x) => x.selected)

  const treatAsModelTransaction = transaction?.what_if_id && !modelId

  let type =
    transaction?.transaction_type === TRANSACTION_TYPES.TRANSFER
      ? 'transfer'
      : transaction?.destination_type === TRANSACTION_DIRECTION.MONEY_IN
        ? 'in'
        : transaction?.destination_type === TRANSACTION_DIRECTION.MONEY_OUT
          ? 'out'
          : null

  function navigateToEdit({ series = false }: { series: boolean }) {
    // const type =
    //   transaction?.transaction_type === TRANSACTION_TYPES.TRANSFER
    //     ? 'transfer'
    //     : transaction?.direction === TRANSACTION_DIRECTION.MONEY_IN
    //       ? 'in'
    //       : transaction?.direction === TRANSACTION_DIRECTION.MONEY_OUT
    //         ? 'out'
    //         : undefined
    const base = ['in', 'out'].includes(type) ? 'categories' : 'vaults'
    const id = ['in', 'out'].includes(type)
      ? transaction?.transaction_category_id
      : transaction?.vault_id

    navigate(
      `/${base}/${id}/transactions/${transaction?.id}/edit?type=${type}&editSeries=${series}&offset=${transaction?.offset}`,
    )
  }

  const { updateExistingWindow } = useOpenNonModalWindow()

  async function handleDelete({ deleteAll }) {
    await deleteT({ id: transaction.id, all: deleteAll })
    setShowDeleteConfirmation(false)
    setShowDeleteSeriesConfirmation(false)
  }

  return (
    <>
      <Dialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        // modal={true}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>

          <DialogBody>
            <p>Are you sure you want to delete this transaction?</p>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="destructive"
              onClick={() => handleDelete({ deleteAll: false })}
            >
              Delete
            </Button>

            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDeleteSeriesConfirmation}
        onOpenChange={setShowDeleteSeriesConfirmation}
        // modal={true}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Entire Series</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>

          <DialogBody>
            <p>
              Are you sure you want to delete this entire series of
              transactions?
            </p>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="destructive"
              onClick={() => handleDelete({ deleteAll: true })}
            >
              Delete
            </Button>

            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card
        data-type={treatAsModelTransaction ? 'model' : type}
        className="group w-full rounded-md border border-light shadow-none"
      >
        <CardHeader className="flex flex-row items-start justify-between gap-0 p-3 group-data-[type=in]:bg-green-lowest group-data-[type=model]:bg-accent-lowest group-data-[type=out]:bg-purple-lowest">
          <div>
            <div className="grid  text-balance font-medium group-data-[type=in]:text-txt-success group-data-[type=model]:text-txt-accent group-data-[type=out]:text-txt-purple">
              <div className="flex items-center gap-1">
                {transaction?.recurring_setting_id && (
                  <ArrowPathRoundedSquareIcon className="-ml-0.5 size-4" />
                )}
                <span>
                  {type === 'in' ? 'TO' : 'FROM'}: {vault?.name}
                </span>
              </div>
              {treatAsModelTransaction && (
                <div className="text-xs">
                  Model:{' '}
                  <Link to={`/models/${transaction?.what_if_id}/transactions`}>
                    {model?.name}
                  </Link>
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-txt-primary">
              {format(transaction.amount)}
            </p>
          </div>

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
              <Button
                type="button"
                iconOnly
                variant="outline"
                className="bg-transparent"
              >
                <EllipsisVerticalIcon />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent side="bottom" align="end">
              {/* Edit */}
              {!transaction?.recurring_setting_id && (
                <DropdownMenuItem
                  onClick={() => {
                    navigateToEdit({ series: false })
                  }}
                >
                  <Button variant="ghost">
                    <PencilSquareIcon />
                    <ButtonText>Edit</ButtonText>
                  </Button>
                </DropdownMenuItem>
              )}
              {/* Edit series */}
              {transaction?.recurring_setting_id && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="pl-3">
                    <div className="flex items-center gap-2">
                      <PencilSquareIcon className="size-4 text-txt-secondary" />
                      <ButtonText>Edit</ButtonText>
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => {
                          navigateToEdit({ series: false })

                          // updateExistingWindow({
                          //   editTransaction: transaction,
                          //   rerender: true,
                          //   activeTab: 'new',
                          //   editSeries: false,
                          // })
                        }}
                      >
                        This Only
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          navigateToEdit({ series: true })

                          // updateExistingWindow({
                          //   editTransaction: transaction,
                          //   rerender: true,
                          //   activeTab: 'new',
                          //   editSeries: true,
                          // })
                        }}
                      >
                        Series
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
              {/* Delete */}
              {!transaction?.recurring_setting_id && (
                <DropdownMenuItem>
                  <Button
                    onClick={() => setShowDeleteConfirmation(true)}
                    variant="ghost"
                    className="!text-txt-error"
                  >
                    <TrashIcon className="!text-txt-error" />
                    <ButtonText>Delete</ButtonText>
                  </Button>
                </DropdownMenuItem>
              )}

              {/* Delete Series */}
              {transaction?.recurring_setting_id && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="pl-3">
                    <div className="flex items-center gap-2 text-txt-error">
                      <TrashIcon className="size-4 !text-txt-error" />

                      <ButtonText>Delete</ButtonText>
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => setShowDeleteConfirmation(true)}
                      >
                        This only
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowDeleteSeriesConfirmation(true)}
                      >
                        Series
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent className="!p-3 ">
          <div className="grid grid-cols-2">
            <div className="grid gap-2">
              {category && modelId && (
                <div>
                  <Description className="m-0">Category</Description>
                  <p>{category?.name}</p>
                </div>
              )}
              <div>
                <Description className="m-0">Date</Description>
                {formatDate(
                  new Date(transaction?.transaction_date),
                  'd MMM yyyy',
                )}
              </div>

              <div>
                <Description className="m-0">Description</Description>
                <p>{transaction?.description ?? '—'}</p>
              </div>
            </div>

            <div className="grid gap-2">
              <div>
                <Description className="m-0">Repeat</Description>
                <p>{selectedRecurringSettings?.name}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
